/**
 * CI metadata collector
 *
 * Collects CI/CD environment information across the major providers. Detection
 * mirrors the standalone testdino-cli precedence so a run reports the same
 * provider regardless of which CLI produced it.
 *
 * Provider env var references:
 * - GitHub Actions: https://docs.github.com/en/actions/reference/workflows-and-actions/variables
 * - GitLab CI:      https://docs.gitlab.com/ci/variables/predefined_variables/
 * - CircleCI:       https://circleci.com/docs/reference/variables/
 * - Jenkins:        https://www.jenkins.io/doc/book/pipeline/jenkinsfile/#using-environment-variables
 * - Azure DevOps:   https://learn.microsoft.com/en-us/azure/devops/pipelines/build/variables
 * - AWS CodeBuild:  https://docs.aws.amazon.com/codebuild/latest/userguide/build-env-ref-env-vars.html
 * - Bitbucket:      https://support.atlassian.com/bitbucket-cloud/docs/variables-and-secrets/
 * - Buildkite:      https://buildkite.com/docs/pipelines/configure/environment-variables
 * - Harness:        https://developer.harness.io/docs/platform/variables-and-expressions/harness-variables/
 */

import { type as osType, release as osRelease } from 'os';
import { BaseMetadataCollector } from './base.js';
import { detectCIProvider } from './detect-ci-provider.js';
import type { CIMetadata, GitMetadata, PRMetadata } from './types.js';

/**
 * Provider-specific pipeline + build slice. Environment info is collected once
 * and shared, so providers only resolve the fields that differ between them.
 */
type ProviderMetadata = Pick<CIMetadata, 'pipeline' | 'build'>;

/**
 * Collects CI environment metadata
 */
export class CIMetadataCollector extends BaseMetadataCollector<CIMetadata> {
  constructor() {
    super('ci');
  }

  /**
   * Collect CI metadata
   */
  protected async collectMetadata(): Promise<CIMetadata> {
    const provider = detectCIProvider();

    if (!provider) {
      return {
        provider: 'local',
        environment: this.collectEnvironment(),
      };
    }

    const { pipeline, build } = this.getProviderMetadata(provider);

    return {
      provider,
      pipeline,
      build,
      environment: this.collectEnvironment(),
    };
  }

  /**
   * Get empty metadata
   */
  protected getEmptyMetadata(): CIMetadata {
    return {};
  }

  /**
   * Route to the provider-specific pipeline/build extractor
   */
  private getProviderMetadata(provider: string): ProviderMetadata {
    switch (provider) {
      case 'github-actions':
        return this.getGitHubActionsMetadata();
      case 'gitlab-ci':
        return this.getGitLabCIMetadata();
      case 'circleci':
        return this.getCircleCIMetadata();
      case 'jenkins':
        return this.getJenkinsMetadata();
      case 'azure-devops':
        return this.getAzureDevOpsMetadata();
      case 'aws-codebuild':
        return this.getAWSCodeBuildMetadata();
      case 'bitbucket-pipelines':
        return this.getBitbucketPipelinesMetadata();
      case 'buildkite':
        return this.getBuildkiteMetadata();
      case 'harness':
        return this.getHarnessMetadata();
      default:
        return this.getGenericMetadata();
    }
  }

  /**
   * GitHub Actions pipeline + build
   */
  private getGitHubActionsMetadata(): ProviderMetadata {
    const { env } = process;

    return {
      pipeline: {
        id: env.GITHUB_RUN_ID,
        name: env.GITHUB_WORKFLOW,
        url: this.buildGitHubPipelineUrl(),
        attempt: parseRunAttempt(env.GITHUB_RUN_ATTEMPT),
      },
      build: {
        number: env.GITHUB_RUN_NUMBER,
        trigger: env.GITHUB_EVENT_NAME,
        // GITHUB_ACTOR stays the first attempt's initiator on a re-run.
        actor: env.GITHUB_TRIGGERING_ACTOR || env.GITHUB_ACTOR,
      },
    };
  }

  /**
   * Build the GitHub Actions pipeline URL when all parts are present
   */
  private buildGitHubPipelineUrl(): string | undefined {
    const { env } = process;
    const { GITHUB_SERVER_URL, GITHUB_REPOSITORY, GITHUB_RUN_ID } = env;

    if (GITHUB_SERVER_URL && GITHUB_REPOSITORY && GITHUB_RUN_ID) {
      return `${GITHUB_SERVER_URL}/${GITHUB_REPOSITORY}/actions/runs/${GITHUB_RUN_ID}`;
    }

    return undefined;
  }

  /**
   * GitLab CI pipeline + build
   */
  private getGitLabCIMetadata(): ProviderMetadata {
    const { env } = process;

    return {
      pipeline: {
        id: env.CI_PIPELINE_ID,
        name: this.getGitLabPipelineName(),
        url: env.CI_PIPELINE_URL,
      },
      build: {
        number: env.CI_JOB_ID || env.CI_PIPELINE_IID,
        trigger: env.CI_PIPELINE_SOURCE,
        actor: env.GITLAB_USER_NAME || env.GITLAB_USER_LOGIN,
      },
    };
  }

  /**
   * Derive a human-readable GitLab pipeline name
   */
  private getGitLabPipelineName(): string | undefined {
    const { CI_PIPELINE_SOURCE, CI_COMMIT_TITLE, CI_PIPELINE_NAME } = process.env;

    if (CI_PIPELINE_SOURCE) return `${CI_PIPELINE_SOURCE} pipeline`;

    return CI_COMMIT_TITLE || CI_PIPELINE_NAME || undefined;
  }

  /**
   * CircleCI pipeline + build
   */
  private getCircleCIMetadata(): ProviderMetadata {
    const { env } = process;

    return {
      pipeline: {
        id: env.CIRCLE_PIPELINE_ID || env.CIRCLE_BUILD_NUM,
        name: env.CIRCLE_JOB,
        url: env.CIRCLE_BUILD_URL,
      },
      build: {
        number: env.CIRCLE_BUILD_NUM,
        trigger: this.getCircleCIBuildTrigger(),
      },
    };
  }

  /**
   * Determine CircleCI build trigger from available env vars
   */
  private getCircleCIBuildTrigger(): string {
    const { env } = process;

    if (env.CIRCLE_TAG) return 'tag';
    if (env.CIRCLE_PR_NUMBER || env.CIRCLE_PULL_REQUEST) return 'pr';

    return 'push';
  }

  /**
   * Jenkins pipeline + build
   */
  private getJenkinsMetadata(): ProviderMetadata {
    const { env } = process;

    return {
      pipeline: {
        id: env.BUILD_ID,
        name: env.JOB_NAME,
        url: env.BUILD_URL,
      },
      build: {
        number: env.BUILD_NUMBER,
        trigger: this.getJenkinsBuildTrigger(),
      },
    };
  }

  /**
   * Jenkins doesn't expose a single BUILD_CAUSE env var — it exposes each cause
   * as a separate boolean (BUILD_CAUSE_MANUALTRIGGER, BUILD_CAUSE_SCMTRIGGER,
   * etc.) that the Groovy env-vars plugin sets. Prefer these; fall back to the
   * legacy BUILD_CAUSE if a customer sets it manually in their pipeline.
   */
  private getJenkinsBuildTrigger(): string | undefined {
    const { env } = process;

    if (env.BUILD_CAUSE_MANUALTRIGGER === 'true') return 'manual';
    if (env.BUILD_CAUSE_SCMTRIGGER === 'true') return 'push';
    if (env.BUILD_CAUSE_TIMERTRIGGER === 'true') return 'scheduled';
    if (env.BUILD_CAUSE_UPSTREAMTRIGGER === 'true') return 'upstream';
    if (env.BUILD_CAUSE_REMOTECAUSE === 'true') return 'remote';

    return env.BUILD_CAUSE;
  }

  /**
   * Azure DevOps pipeline + build
   */
  private getAzureDevOpsMetadata(): ProviderMetadata {
    const { env } = process;

    return {
      pipeline: {
        id: env.BUILD_BUILDID,
        name: env.BUILD_DEFINITIONNAME,
        url: this.buildAzureDevOpsPipelineUrl(),
      },
      build: {
        number: env.BUILD_BUILDNUMBER,
        trigger: env.BUILD_REASON,
        actor: env.BUILD_REQUESTEDFOR,
      },
    };
  }

  /**
   * Construct the Azure DevOps build-results URL when all parts are present
   */
  private buildAzureDevOpsPipelineUrl(): string | undefined {
    const { SYSTEM_TEAMFOUNDATIONSERVERURI, SYSTEM_TEAMPROJECT, BUILD_BUILDID } = process.env;

    if (!SYSTEM_TEAMFOUNDATIONSERVERURI || !SYSTEM_TEAMPROJECT || !BUILD_BUILDID) {
      return undefined;
    }

    try {
      const baseUrl = SYSTEM_TEAMFOUNDATIONSERVERURI.replace(/\/$/, '');
      const encodedProject = encodeURIComponent(SYSTEM_TEAMPROJECT);
      return `${baseUrl}/${encodedProject}/_build/results?buildId=${BUILD_BUILDID}`;
    } catch {
      return undefined;
    }
  }

  /**
   * AWS CodeBuild pipeline + build
   */
  private getAWSCodeBuildMetadata(): ProviderMetadata {
    const { env } = process;
    const buildId = env.CODEBUILD_BUILD_ID || '';
    // Build ID format is `project-name:build-uuid`
    const projectName = buildId.split(':')[0] || undefined;

    return {
      pipeline: {
        id: env.CODEBUILD_BUILD_ID,
        name: projectName,
        url: env.CODEBUILD_BUILD_URL,
      },
      build: {
        number: env.CODEBUILD_BUILD_NUMBER,
        trigger: this.getAWSCodeBuildTrigger(),
      },
    };
  }

  /**
   * Determine the AWS CodeBuild trigger from webhook/initiator env vars
   */
  private getAWSCodeBuildTrigger(): string | undefined {
    const { env } = process;

    if (env.CODEBUILD_WEBHOOK_EVENT) return env.CODEBUILD_WEBHOOK_EVENT;

    if (env.CODEBUILD_INITIATOR) {
      return env.CODEBUILD_INITIATOR.startsWith('codepipeline/') ? 'codepipeline' : 'manual';
    }

    return undefined;
  }

  /**
   * Bitbucket Pipelines pipeline + build
   */
  private getBitbucketPipelinesMetadata(): ProviderMetadata {
    const { env } = process;
    const { BITBUCKET_WORKSPACE, BITBUCKET_REPO_SLUG, BITBUCKET_BUILD_NUMBER } = env;

    const url =
      BITBUCKET_WORKSPACE && BITBUCKET_REPO_SLUG && BITBUCKET_BUILD_NUMBER
        ? `https://bitbucket.org/${BITBUCKET_WORKSPACE}/${BITBUCKET_REPO_SLUG}/pipelines/results/${BITBUCKET_BUILD_NUMBER}`
        : undefined;

    let trigger = 'push';
    if (env.BITBUCKET_PR_ID) {
      trigger = 'pull_request';
    } else if (env.BITBUCKET_TAG) {
      trigger = 'tag';
    }

    return {
      pipeline: {
        id: env.BITBUCKET_PIPELINE_UUID,
        name: 'Bitbucket Pipeline',
        url,
      },
      build: {
        number: BITBUCKET_BUILD_NUMBER,
        trigger,
      },
    };
  }

  /**
   * Buildkite pipeline + build
   */
  private getBuildkiteMetadata(): ProviderMetadata {
    const { env } = process;

    return {
      pipeline: {
        id: env.BUILDKITE_PIPELINE_ID || env.BUILDKITE_PIPELINE_SLUG,
        name: env.BUILDKITE_PIPELINE_NAME || env.BUILDKITE_PIPELINE_SLUG,
        url: env.BUILDKITE_BUILD_URL,
      },
      build: {
        number: env.BUILDKITE_BUILD_NUMBER,
        trigger: this.getBuildkiteTrigger(),
        actor: env.BUILDKITE_BUILD_CREATOR,
      },
    };
  }

  /**
   * Determine the Buildkite trigger. BUILDKITE_PULL_REQUEST is the PR number or
   * the literal string 'false' when the build is not for a PR.
   *
   * BUILDKITE_SOURCE describes what started the build (webhook, ui, api,
   * schedule, trigger_job). It's normalized to the same push/manual/scheduled
   * vocabulary the other providers emit — an ordinary git push arrives as
   * 'webhook', which would otherwise leak as a trigger value. Unknown sources
   * pass through so no signal is lost.
   */
  private getBuildkiteTrigger(): string {
    const { env } = process;

    if (env.BUILDKITE_TAG) return 'tag';
    if (env.BUILDKITE_PULL_REQUEST && env.BUILDKITE_PULL_REQUEST !== 'false') return 'pull_request';

    switch (env.BUILDKITE_SOURCE) {
      case undefined:
      case '':
      case 'webhook':
        return 'push';
      case 'ui':
        return 'manual';
      case 'schedule':
        return 'scheduled';
      default:
        return env.BUILDKITE_SOURCE;
    }
  }

  /**
   * Harness CI pipeline + build.
   *
   * Harness CI is built on Drone and mirrors the documented DRONE_* vars into
   * the step container; those are the reliable source for build number and
   * trigger. The HARNESS_* vars carry pipeline identity. The results URL is
   * account/module-specific and not reliably reconstructable from env, so it is
   * left undefined. `name` is omitted rather than duplicating the identifier —
   * Harness exposes no distinct pipeline-name env var.
   */
  private getHarnessMetadata(): ProviderMetadata {
    const { env } = process;

    return {
      pipeline: {
        id: env.HARNESS_PIPELINE_ID,
      },
      build: {
        number: env.HARNESS_BUILD_ID || env.DRONE_BUILD_NUMBER,
        trigger: this.getHarnessTrigger(),
      },
    };
  }

  /**
   * Normalize the Harness/Drone build event. Drone emits 'cron' for scheduled
   * builds; align it with the 'scheduled' vocabulary the other providers use.
   * Other events (push, pull_request, tag) pass through unchanged.
   */
  private getHarnessTrigger(): string | undefined {
    const event = process.env.DRONE_BUILD_EVENT;
    if (!event) return undefined;
    return event === 'cron' ? 'scheduled' : event;
  }

  /**
   * Fallback for a detected-but-unmapped CI provider.
   *
   * Kept intentionally narrow: only reads env vars that are truly widely-shared
   * across generic CI images (CI_BUILD_NUMBER, BUILD_NUMBER). Does NOT leak
   * GitLab-specific vars (CI_PIPELINE_ID / CI_PIPELINE_NAME / CI_PIPELINE_URL /
   * CI_PIPELINE_SOURCE), which would falsely attribute an unknown provider's
   * run to GitLab semantics in the dashboard.
   */
  private getGenericMetadata(): ProviderMetadata {
    const { env } = process;

    return {
      pipeline: {},
      build: {
        number: env.CI_BUILD_NUMBER || env.BUILD_NUMBER,
      },
    };
  }

  /**
   * Collect runner environment information
   */
  private collectEnvironment(): CIMetadata['environment'] {
    try {
      return {
        name: osType(),
        type: process.platform,
        os: `${osType()} ${osRelease()}`,
        node: process.version,
      };
    } catch {
      return {};
    }
  }

  /**
   * Whether the person who started the run wrote the commit, from evidence only
   * (an account login or matching emails); left unset when the provider cannot tell.
   */
  static resolveActorIsAuthor(ci: CIMetadata, git: GitMetadata): void {
    const verifiedLogin = git.commit?.verifiedLogin;
    if (git.commit) delete git.commit.verifiedLogin;

    const actor = ci.build?.actor;
    if (!ci.build || !actor) return;

    const { env } = process;
    const commitEmail = git.commit?.email;
    const sameEmail = (a?: string, b?: string): boolean | undefined =>
      a && b ? a.toLowerCase() === b.toLowerCase() : undefined;

    let result: boolean | undefined;
    switch (ci.provider) {
      case 'github-actions':
        result = verifiedLogin
          ? verifiedLogin.toLowerCase() === actor.toLowerCase()
          : CIMetadataCollector.isNoreplyEmailOf(commitEmail, actor) || undefined;
        break;
      case 'gitlab-ci':
        result = sameEmail(env.GITLAB_USER_EMAIL, commitEmail);
        break;
      case 'azure-devops':
        result = sameEmail(env.BUILD_REQUESTEDFOREMAIL, commitEmail);
        break;
      case 'buildkite':
        result = sameEmail(env.BUILDKITE_BUILD_CREATOR_EMAIL, env.BUILDKITE_BUILD_AUTHOR_EMAIL || commitEmail);
        break;
    }
    if (result !== undefined) ci.build.actorIsAuthor = result;
  }

  // GitHub's `{id}+{login}@users.noreply.github.com` (or the older `{login}@...`) belongs to that login.
  private static isNoreplyEmailOf(email: string | undefined, login: string): boolean {
    if (!email) return false;
    const match = /^(?:\d+\+)?([^@]+)@users\.noreply\.github\.com$/i.exec(email);
    return match !== null && match[1].toLowerCase() === login.toLowerCase();
  }

  /**
   * Reconcile CI and Git metadata after both collectors complete.
   *
   * Bitbucket Pipelines runs that fire on the target branch after a PR merge do
   * not expose `BITBUCKET_PR_ID`, so env-only detection reports `push`. The
   * auto-merge commit subject (`Merged in <branch> (pull request #N)`) is the
   * reliable signal that the push originated from a PR — when present we surface
   * the run as a `pull_request` trigger and backfill PR number/url/status/title.
   *
   * No-op for every other provider.
   */
  static reconcileWithGit(ci: CIMetadata, git: GitMetadata): void {
    if (ci.provider !== 'bitbucket-pipelines') return;
    if (ci.build?.trigger !== 'push') return;

    const message = git.commit?.message || '';
    // Anchored to Bitbucket's auto-merge subject so we don't false-positive on
    // commits that merely quote the phrase (e.g. `Revert "Merged in ..."`).
    const match = message.match(/^Merged in [^\n]+\(pull request #(\d+)\)/i);
    const prId = match?.[1];
    if (!prId) return;

    if (ci.build) ci.build.trigger = 'pull_request';

    const pr: PRMetadata = git.pr ?? {};

    if (pr.number === undefined) {
      pr.number = Number(prId);
      if (git.repository?.url) {
        const base = git.repository.url.replace(/\.git$/, '').replace(/\/$/, '');
        pr.url = `${base}/pull-requests/${prId}`;
      }
      pr.status = 'merged';
      pr.merged = true;
    }

    // The auto-merge subject is the only place the PR title lives for post-merge
    // runs; use it when nothing better is available.
    if (!pr.title) {
      const subject = message.split('\n')[0]?.trim();
      if (subject) pr.title = subject;
    }

    git.pr = pr;
  }
}

// The server reads only a positive integer, so anything else is omitted.
export function parseRunAttempt(value: string | undefined): number | undefined {
  return value !== undefined && /^[1-9]\d*$/.test(value) ? Number(value) : undefined;
}
